import { useSessionContext } from "@/app/[tenantId]/[messengerId]/[hostConfig]/[locale]/lib/client/contexts/SessionContext";
import getInitialPageForUser from "@/app/[tenantId]/[messengerId]/[hostConfig]/[locale]/lib/client/utils/getInitialPageForUser";
import { useLauncherContext } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/contexts/LauncherContext";
import { useLauncherStore } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/contexts/LauncherStoreContext";
import { getIframeDimensions } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/utils/getIframeDimensions";
import type { ToOptions } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import type { IconType } from "../ui/Icon";
import { Icon } from "../ui/Icon";
import { Spinner } from "../ui/Spinner";
import UnreadBadge from "./UnreadBadge";
import { usePermalink } from "@/lib/client/hooks/usePermalink";
import { useTranslations } from "@/app/[tenantId]/[messengerId]/[hostConfig]/[locale]/lib/client/contexts/TranslationContext";
import { flushSync } from "react-dom";
import { getMessengerToken } from "@/lib/client/utils/config";
import { ProactiveChat } from "./ProactiveChat";
import {
  useActiveConversation,
  useLastVisitedTab,
  useWasClosed,
} from "@/app/[tenantId]/[messengerId]/[hostConfig]/[locale]/lib/client/contexts/DeviceContext";

const DefaultIconMap: { [key: string]: IconType } = {
  "book-open": "dazzle-solid-book-open",
  "message-circle-dots": "dazzle-solid-message-circle-dots",
  mail: "dazzle-solid-mail",
  user: "dazzle-solid-user",
  agent: "dp-custom-agent",
};

export default function LauncherButton({
  messengerId,
  isAuth,
  params,
}: {
  params: {
    tenantId: string;
    messengerId: string;
    hostConfig: string;
    locale: string;
  };
  messengerId: string;
  /** The launcher button rendered within the auth routes (i.e home, messages etc). The auth button has a session and socket available */
  isAuth?: boolean;
}) {
  const { showLauncherButton, isOpen, config, isFullscreen } = useLauncherStore(
    useShallow((state) => ({
      showLauncherButton: state.showLauncherButton,
      isOpen: state.isOpen,
      config: state.config,
      isFullscreen: state.isFullscreen,
    })),
  );
  const [proactiveChatIsOpen, setProactiveChatIsOpen] = useState(false);
  const proactiveChatWasShown = useRef(false); // Show once
  const messengerWidgetWasOpen = useRef(false);

  const side =
    config?.settings?.displaySettings.placement?.position === "bottom-left" ? "left" : "right";

  const offsetBottom = config?.settings?.displaySettings.placement?.offsetBottom ?? 0;
  const offsetSide = config?.settings?.displaySettings.placement?.offsetSide ?? 0;

  useEffect(() => {
    if (isOpen) {
      messengerWidgetWasOpen.current = true;
    }
  }, [isOpen]);

  if (!showLauncherButton || (isFullscreen && isOpen)) {
    return null;
  }

  return (
    <div
      className={clsx("flex items-end justify-end gap-2", side === "left" && "flex-row-reverse")}
    >
      {config?.settings.launcherSettings.type === "proactive" ? (
        <ProactiveChat
          side={side}
          isMessengerWidgetOpen={isOpen}
          messengerId={messengerId}
          offsetBottom={offsetBottom}
          offsetSide={offsetSide}
          proactiveChatIsOpen={proactiveChatIsOpen}
          setProactiveChatIsOpen={setProactiveChatIsOpen}
          proactiveChatWasShown={proactiveChatWasShown}
          messengerWidgetWasOpen={messengerWidgetWasOpen}
        />
      ) : undefined}
      <LauncherLink
        isAuth={isAuth}
        params={params}
        messengerId={messengerId}
        offsetBottom={offsetBottom}
        offsetSide={offsetSide}
      />
    </div>
  );
}

const ClosedIcon = ({
  icon,
  isAuth,
}: {
  icon:
    | {
        type: "icon";
        icon: IconType;
      }
    | { type: "file"; src: string };
  isAuth?: boolean;
}) => {
  return (
    <div className="relative flex items-center justify-center">
      {isAuth ? <UnreadBadge /> : null}
      {icon.type === "icon" ? (
        <Icon icon={icon.icon} size="text-[32px]" color="text-white" />
      ) : (
        <img
          width={40}
          height={40}
          className="max-w-none"
          src={icon.src}
          alt="chat launcher button"
        />
      )}
    </div>
  );
};

const OpenIcon = () => {
  const session = useSessionContext().session;

  return session || isLoginRoute() ? (
    <Icon icon="tailwind-ui-outline-chevron-down" size="text-4xl" color="text-white" />
  ) : (
    <Spinner size={"sm"} color="inverse" />
  );
};

const isLoginRoute = () => window.location.pathname.includes("login");

const LauncherLink = memo(
  ({
    isAuth,
    params,
    messengerId,
    offsetBottom,
    offsetSide,
  }: {
    isAuth?: boolean;
    params: {
      tenantId: string;
      messengerId: string;
      hostConfig: string;
      locale: string;
    };
    messengerId: string;
    offsetBottom: number;
    offsetSide: number;
  }) => {
    const t = useTranslations();
    const { connection } = useLauncherContext();
    const {
      showLauncherButton,
      isOpen,
      setIsOpen,
      isOpenAnimating,
      setIsOpenAnimating,
      config,
      goTo,
      setIsExpanded,
      setIsFullscreen,
      overrideLocale,
    } = useLauncherStore(
      useShallow((state) => ({
        showLauncherButton: state.showLauncherButton,
        isOpen: state.isOpen,
        setIsOpen: state.setIsOpen,
        isOpenAnimating: state.isOpenAnimating,
        setIsOpenAnimating: state.setIsOpenAnimating,
        setIsExpanded: state.setIsExpanded,
        config: state.config,
        goTo: state.goTo,
        setIsFullscreen: state.setIsFullscreen,
        overrideLocale: state.overrideLocale,
      })),
    );
    const { setWasClosed } = useWasClosed();
    const { lastVisitedTab } = useLastVisitedTab();
    const {
      activeConversations: { activeConversations },
    } = useActiveConversation();

    // On first open from the non auth launch button, link to the locale route, which will redirect to the default tab
    // On close, using empty string will prevent the route from changing
    const toOptions: ToOptions = useMemo(
      () =>
        !isOpen
          ? getInitialPageForUser({
              tenantId: params.tenantId,
              messengerId: params.messengerId,
              hostConfig: params.hostConfig,
              locale: overrideLocale ?? params.locale,
              config,
              goTo,
              lastVisitedTab,
              activeConversations,
            })
          : "",
      [
        isOpen,
        params.tenantId,
        params.messengerId,
        params.hostConfig,
        params.locale,
        overrideLocale,
        config,
        goTo,
        lastVisitedTab,
        activeConversations,
      ],
    );

    const { createPermalinkPath } = usePermalink({
      tenantId: params.tenantId,
      messengerId: params.messengerId,
      messengerToken: getMessengerToken(params.hostConfig),
    });

    const icon = useMemo(() => {
      // if for some reason the icon object is not set in config, use the default icon
      if (!config?.settings?.launcherSettings?.icon) {
        return {
          type: "icon" as const,
          icon: "dazzle-solid-message-circle-dots" as IconType,
        };
      }

      const iconType = config.settings.launcherSettings.icon.type;
      const icon = config.settings.launcherSettings.icon.icon;
      const fileUuid = config.settings.launcherSettings.icon.file?.fileUuid;

      if (iconType === "file" && fileUuid) {
        const launcherIconUrl = createPermalinkPath("launcher-icon", fileUuid);

        return {
          type: "file" as const,
          src: launcherIconUrl,
        };
      }

      return {
        type: "icon" as const,
        icon: icon ? DefaultIconMap[icon] : ("dazzle-solid-message-circle-dots" as IconType),
      };
    }, [config, createPermalinkPath]);

    const toggleOpen = useCallback(
      async (open: boolean) => {
        if (!connection?.current) return;
        const parentApi = await connection.current.promise;
        const parentViewDimensions = await parentApi.getViewDimensions(messengerId);

        const data = getIframeDimensions({
          isOpen: open,
          showLauncherButton,
          offsetBottom,
          offsetSide,
          parentViewDimensions,
        });
        setIsFullscreen(data.type === "fullscreen");

        return isOpen
          ? await parentApi.close(messengerId, data)
          : await parentApi.open(messengerId, data);
      },
      [
        connection,
        isOpen,
        messengerId,
        offsetBottom,
        offsetSide,
        setIsFullscreen,
        showLauncherButton,
      ],
    );
    const handleClick = useCallback(async () => {
      if (isOpenAnimating) return;
      flushSync(() => {
        setIsOpenAnimating(true);
      });

      // Prevent the widget auto re-opening on refresh
      setWasClosed(isOpen);

      if (showLauncherButton && isOpen) {
        setIsOpen(false);
        setIsExpanded(false);
        await toggleOpen(false);
      } else {
        setIsOpen(true);
        await toggleOpen(true);
      }
      setIsOpenAnimating(false);
    }, [
      isOpenAnimating,
      showLauncherButton,
      isOpen,
      setIsOpenAnimating,
      setIsExpanded,
      toggleOpen,
      setIsOpen,
      setWasClosed,
    ]);

    return (
      <Link
        data-isauth={String(!!isAuth)}
        className={clsx(
          "launcherButton",
          "group transition-all duration-200 ease-out hover:scale-110",
          "p2 flex items-center justify-center self-end justify-self-end rounded-full border-0 shadow-lg shadow-black/20",
          "h-14 max-h-[3.5rem] min-h-[3.5rem] w-14 min-w-[3.5rem] max-w-[3.5rem]",
          "bg-[--launcherButtonColor] hover:brightness-95",
        )}
        to={toOptions.to}
        params={toOptions.params}
        onClick={handleClick}
        aria-label={t("LauncherButton.aria_label")}
      >
        <div className="relative flex items-center justify-center">
          <div
            className={clsx(
              "absolute transition-all duration-200 ease-out",
              isOpen ? "scale-100 opacity-100" : "scale-75 opacity-0",
            )}
          >
            <OpenIcon />
          </div>
          <div
            className={clsx(
              "absolute transition-all duration-100 ease-out",
              isOpen ? "scale-75 opacity-0" : "scale-100 opacity-100",
            )}
          >
            <ClosedIcon icon={icon} isAuth={isAuth} />
          </div>
        </div>
      </Link>
    );
  },
);
